import { Nullable } from 'global';
import { BasePrice } from 'modules/equipment/types';
import { QuoteCalcRowInput } from '@trnsact/trnsact-shared-types';

import { QuoteChargeListingEntity, QuoteLineItemType } from '../types';
import { QuoteCalcRowPrice, QuoteCalcViewModel } from '../api/types';

class QuoteCalcMapper {
  public toPreviewRows(rows: QuoteChargeListingEntity[] = []): QuoteCalcRowInput[] {
    return rows.map(row => {
      if (row.type === QuoteLineItemType.Equipment && row.equipment) {
        return {
          type: QuoteLineItemType.Equipment,
          equipment: {
            equipmentId: row.equipment.equipmentId,
            price: this.toCalcPrice(row.equipment.price),
            attachments: (row.equipment.attachments ?? []).map(attachment => ({
              price: this.toCalcPrice(attachment.price),
            })),
          },
        };
      }

      const charge = row.charge;

      return {
        type: QuoteLineItemType.Invoice,
        charge: charge
          ? {
              type: charge.type,
              id: charge.id ?? null,
              label: charge.label ?? null,
              amount: charge.amount ?? null,
              parentId: charge.parentId ?? null,
              isTaxable: charge.isTaxable ?? null,
              amountType: charge.amountType ?? null,
              parentType: charge.parentType ?? null,
              isFromCommonRules: charge.isFromCommonRules ?? false,
              customerVisibility: charge.customerVisibility ?? null,
              price: this.toCalcPrice(charge.price),
              groupBucket: {
                id: charge.groupBucket.id,
                type: charge.groupBucket.type,
              },
            }
          : null,
      };
    });
  }

  public applyCalcToRows(
    rows: QuoteChargeListingEntity[] = [],
    calc: Nullable<QuoteCalcViewModel>
  ): QuoteChargeListingEntity[] {
    if (!calc?.rows?.length) return rows;

    return rows.map((row, index) => {
      const calcRow = calc.rows[index];

      if (!calcRow) return row;

      if (row.type === QuoteLineItemType.Equipment && row.equipment && calcRow.equipment) {
        const attachments = (row.equipment.attachments ?? []).map((attachment, attachmentIndex) => {
          const calcAttachment = calcRow.equipment?.attachments?.[attachmentIndex];

          return calcAttachment
            ? { ...attachment, price: this.mergeCalcPrice(attachment.price, calcAttachment.price) }
            : attachment;
        });

        return {
          ...row,
          equipment: {
            ...row.equipment,
            attachments,
            price: this.mergeCalcPrice(row.equipment.price, calcRow.equipment.price),
          },
        };
      }

      if (row.type === QuoteLineItemType.Invoice && row.charge && calcRow.charge) {
        return {
          ...row,
          charge: {
            ...row.charge,
            price: this.mergeCalcPrice(row.charge.price, calcRow.charge.price),
          },
        };
      }

      return row;
    });
  }

  private mergeCalcPrice(price: BasePrice, calcPrice: QuoteCalcRowPrice): BasePrice {
    return {
      ...price,
      total: calcPrice.total ?? price.total,
      netPrice: calcPrice.netPrice ?? price.netPrice,
      retailPrice: calcPrice.retailPrice ?? price.retailPrice,
    };
  }

  private toCalcPrice = (price: BasePrice) => ({
    total: price.total ?? 0,
    markup: price.markup ?? 0,
    netPrice: price.netPrice ?? 0,
    discount: price.discount ?? 0,
    salesTax: price.salesTax ?? 0,
    markupType: price.markupType,
    discountType: price.discountType,
    salesTaxType: price.salesTaxType,
    retailPrice: price.retailPrice ?? 0,
    wholesalePrice: price.wholesalePrice ?? 0,
  });
}

export const quoteCalcMapper = new QuoteCalcMapper();
